'use client'

import { useState, useCallback, useEffect } from 'react'

import _isEmpty from 'lodash/isEmpty'
import { type PushSubscription as WebPushSubscription } from 'web-push'

import { showSuccessToast, showErrorToast } from '@/components/ui/toaster'
import ErrorHandler from '@/lib/error/handling'
import { isPushNotificationSupported } from '@/lib/html/devices'
import { isServerSide } from '@/lib/html/server-side'
import { serializeObject } from '@/lib/json/serialization'
import { urlBase64ToUint8Array } from '@/lib/string/base64'
import { subscribeUser, unsubscribeUser } from '@/server/actions/notifications'

import useAsyncEffect from '../use-async-effect'
import useStorage from '../use-storage'
import { USER_AUTH_STORAGE_KEY } from '../use-storage/use-storage.constants'

import { type PushNotificationPermission } from './use-push-notifications.d'
import { isSubscriptionValid, isVapidKeyMatching } from './use-push-notifications.utils'

export function usePushNotifications() {
  const { value: user, storageLoaded } = useStorage<UserAuth>(USER_AUTH_STORAGE_KEY, null)
  const [isSupported, setIsSupported] = useState(false)
  const [subscription, setSubscription] = useState<PushSubscription | null>(null)
  const isSubscribed = !!subscription
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null)

  const [loading, setLoading] = useState(false)
  const [subscribing, setSubscribing] = useState(false)
  const [unsubscribing, setUnsubscribing] = useState(false)

  function getCurrentPermission(): PushNotificationPermission {
    if (isServerSide()) {
      return 'denied'
    }

    if (typeof Notification === 'undefined') {
      return 'denied'
    }

    return Notification.permission
  }

  useEffect(() => {
    if (storageLoaded) {
      setIsSupported(isPushNotificationSupported() && !_isEmpty(user))
    }
  }, [storageLoaded])

  useAsyncEffect(async () => {
    if (!isSupported) {
      return
    }

    try {
      setLoading(true)

      const reg = await navigator.serviceWorker.ready

      const sub = await reg.pushManager.getSubscription()

      if (isSubscriptionValid(sub)) {
        const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || ''
        if (vapidPublicKey && !isVapidKeyMatching(sub!, vapidPublicKey)) {
          await sub!.unsubscribe()
          await unsubscribeUser(sub!.endpoint)
        } else {
          setSubscription(sub)
          await subscribeUser(serializeObject(sub) as WebPushSubscription)
        }
      }
      setRegistration(reg)
    } catch (error) {
      ErrorHandler.handle(error as Error)
    } finally {
      setLoading(false)
    }
  }, [isSupported])

  const requestPermission = useCallback(
    async function (): Promise<boolean> {
      if (!isSupported) {
        return false
      }

      try {
        const result = await Notification.requestPermission()
        return result === 'granted'
      } catch (error) {
        ErrorHandler.handle(error as Error)
        return false
      }
    },
    [isSupported]
  )

  const subscribe = useCallback(
    async function (): Promise<void> {
      if (!isSupported || !registration) {
        return
      }

      setSubscribing(true)

      try {
        const permission = getCurrentPermission()
        const hasPermission = permission === 'granted' || (await requestPermission())
        if (!hasPermission) {
          showErrorToast('Notification permission is required')
          return
        }

        const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || ''
        const newSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
        })

        setSubscription(newSubscription)
        await subscribeUser(serializeObject(newSubscription) as WebPushSubscription)
        showSuccessToast('Successfully subscribed to notifications!')
      } catch {
        showErrorToast('Failed to subscribe to notifications')
      } finally {
        setSubscribing(false)
      }
    },
    [isSupported, registration, requestPermission]
  )

  const unsubscribe = useCallback(
    async function (): Promise<void> {
      if (!subscription) {
        return
      }

      try {
        setUnsubscribing(true)
        await subscription.unsubscribe()

        await unsubscribeUser(subscription.endpoint)
        setSubscription(null)
        showSuccessToast('Successfully unsubscribed from notifications')
      } catch {
        showErrorToast('Failed to unsubscribe from notifications')
      } finally {
        setUnsubscribing(false)
      }
    },
    [subscription]
  )

  return {
    subscription,
    isSubscribed,
    isSupported,
    getCurrentPermission,

    loading,
    subscribing,
    unsubscribing,

    subscribe,
    unsubscribe,
    requestPermission,
  }
}
